import React, { useEffect, useRef, useState } from "react";
import { TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import BackArrow from "../../assets/svg/back-arrow.svg";
import CoversureLogo from "../../assets/svg/coversure-logo.svg";
import PrimaryButton from "../../components/Button/PrimaryButton";
import "./style.scss";
import { AppDispatch } from "../../store/store";
import {
  fetchResendOTP,
  handleVerifyOTP,
} from "../../store/reducers/prosure/apiThunk";
import { useNavigate } from "react-router-dom";
import ROUTE_PATH from "../../constant/route";
import { showToast } from "../../components/Toast";
import { updateOTPDone, updatePayment, updateVerified } from "../../store/reducers/prosure";

const VerifyOTP = () => {
  const userData = useSelector((state: any) => state);
  const {
    prosure: { formDetail, prosureUserId },
    home: { totalPrice },
  } = userData;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef<number>(60);
  const [resendOtp, setResendOtp] = useState(false)

  useEffect(() => {
    if (Number(totalPrice) === 0) {
      navigate(ROUTE_PATH.HOME);
    } else if (Object.keys(formDetail)?.length === 0) {
      navigate(ROUTE_PATH.CUSTOMER_DETAILS);
    }
  }, []);
  // Manage the timer
  useEffect(() => {
    const interval = setInterval(() => {
      if (timerRef.current > 0) {
        timerRef.current -= 1;
        setTimer(timerRef.current); // Update state only when necessary
      } else {
        setCanResend(true);
        clearInterval(interval);
      }
      setResendOtp(false)
    }, 1000);

    return () => clearInterval(interval); // Cleanup on component unmount
  }, [resendOtp]); // Empty dependency array ensures this effect runs only once

  const handleResend = async () => {
    const data = {
      mobileNo: formDetail?.mobileNo,
      prosureCustomerId: prosureUserId,
    };
    try {
      const res = await dispatch(fetchResendOTP(data)).unwrap(); // Unwraps to return raw result or throw error
      if (res?.status === 1) {
        setTimer(60);
        timerRef.current = 60
        setResendOtp(true)
        setCanResend(false);
      }
    } catch (error) {
      showToast("Something went wrong", "error");
    }
  };

  const handleChange = (index: number, value: string) => {
    if (isNaN(Number(value))) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length < 6) {
      setError(true);
    } else {
      setError(false);
      const data = {
        mobileNumber: formDetail?.mobileNo,
        otp: otpCode,
        prosureCustomerId: prosureUserId,
        productInfo: "ProSure",
        amount: totalPrice,
      };
      try {
        const res = await dispatch(handleVerifyOTP(data)).unwrap(); // Unwraps to return raw result or throw error
        if (res?.status === 1) {
          navigate(ROUTE_PATH.SUCCESS_OTP);
          dispatch(updateVerified(true));
          dispatch(updatePayment(res?.data));
          dispatch(updateOTPDone(true))
        } else {
          navigate(ROUTE_PATH.FAILURE_OTP);
          dispatch(updateVerified(false));
        }
      } catch (error) {
        navigate(ROUTE_PATH.FAILURE_OTP);
        dispatch(updateVerified(false));
        console.error("Error saving user data:", error);
      }
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === "Backspace" && otp[index] === "") {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  return (
    <div className="verify-otp-css sm:flex items-center justify-center w-[100%] h-[100vh] bg-bg-color-3 sm:bg-bg-color-5">
      <div className="bg-bg-color-3 py-[14px] px-[16px] sm:py-[60px] sm:px-[88px] sm:rounded-[30px] bg-bg-color-3 sm:shadow-custom-light">
        <img
          src={BackArrow}
          alt="BackArrow"
          className="block sm:hidden relative left-[-5%]"
          onClick={() => navigate(-1)}
        />
        <div className="sm:flex flex-col items-center justify-center sm:mb-[36px] mb-[24px]">
          <img
            src={CoversureLogo}
            alt="CoversureLogo"
            className="hidden sm:block h-[22px]"
          />
          <div className="sm:text-[34px] text-[16px] sm:leading-[45px] leading-[20px] sm:font-semibold font-bold text-text-color-6 py-[12px] sm:pt-[26px] sm:pb-[9px]">
            Verify OTP
          </div>
          <div className="sm:text-[16px] text-[12px] sm:leading-[26px] leading-[18px] font-bold sm:font-normal text-text-color-1 sm:pb-[26px] pb-[24px]">
            Enter the 6 digit verification code sent to your mobile number
          </div>
          <div className="sm:text-[32px] text-[24px] leading-[28px] text-text-color-6 font-bold">
            {formDetail?.mobileNo}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex space-x-2">
            {otp.map((digit, index) => (
              <TextField
                key={index}
                id={`otp-${index}`}
                type="text"
                value={digit}
                inputProps={{ maxLength: 1, className: "text-center" }}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onChange={(e) => handleChange(index, e.target.value)}
                className="sm:w-[75px] bg-bg-color-4 text-sm font-medium"
                error={error && otp[index] === ""}
              />
            ))}
          </div>
          {error && (
            <div className="text-red-500 text-sm font-semibold">
              Please enter all 6 digits.
            </div>
          )}
          <div className="sm:text-[16px] sm:leading-[16px] font-semibold text-center sm:pt[26px] hidden sm:block">
            Didn’t get the OTP?{" "}
            <span className="text-text-color-5">
              {canResend ? (
                <span
                  className="text-text-color-5 cursor-pointer"
                  onClick={handleResend}
                >
                  Resend
                </span>
              ) : (
                <span className="text-text-color-5">Resend ({timer}s)</span>
              )}
            </span>
          </div>
          <div className="w-[100%] sm:pt-[34px] pt-[22px]">
            <PrimaryButton
              type="submit"
              classname="sm:w-[80%] w-[100%] m-auto"
              label={
                <div className="flex items-center justify-center w-[100%] font-black">
                  <div className="text-[14px] leading-[18px] sm:text-[24px] sm:leading-[30px] ">
                    Pay Now
                  </div>
                </div>
              }
            />
          </div>
          <div className="text-[12px] leading-[16px] font-semibold text-center sm:pt[24px] block sm:hidden">
            Didn’t get the OTP?{" "}
            <span className="text-text-color-5">
              {canResend ? (
                <span
                  className="text-text-color-5 cursor-pointer"
                  onClick={handleResend}
                >
                  Resend
                </span>
              ) : (
                <span className="text-text-color-5">Resend ({timer}s)</span>
              )}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyOTP;
